import {ApiService} from "./api-service";
import {VerificationDto, VerificationChallengeDto} from "./model/verification-dto";
import {PowSolver} from "../pow/pow-solver";
import {InformationCollector} from "../data-collection/information-collector";
import {CaptchaBox} from "../captcha-box";
import {ErrorCode} from "./error/error-code";
import {ErrorModel} from "./error/error-model";
import {generateUuidV4} from "./utils";

export class ApiManager {
  private captchaBox: CaptchaBox;
  private successCallback: (verificationToken: string, expiresInMs: number) => void;
  private failureCallback: (error: ErrorModel) => void;
  private abortController: AbortController;
  private powSolver: PowSolver;
  private isReset: boolean = false;

  constructor(captchaBox: CaptchaBox, successCallback: (verificationToken: string, expiresInMs: number) => void, failureCallback: (error: ErrorModel) => void) {
    this.captchaBox = captchaBox;
    this.successCallback = successCallback;
    this.failureCallback = failureCallback;
    this.abortController = new AbortController();
    this.powSolver = new PowSolver();
  }

  public async verify(): Promise<void> {
    try {
      const data = await new InformationCollector(this.captchaBox).collectInformation();
      if (this.isReset) return;

      const verificationResponse = await ApiService.createVerification(this.captchaBox.config, data, this.abortController.signal);
      if (this.isReset) return;

      switch (verificationResponse.status) {
        case 200: {
          const response: VerificationDto = await verificationResponse.json();
          if (this.isReset) return;
          this.solveChallenges(response.challenge!);
          return;
        }
        case 201: {
          const response: VerificationDto = await verificationResponse.json();
          if (this.isReset) return;
          this.successCallback(response.finished!.verificationToken, response.finished!.expiresInMs);
          return;
        }
        case 402:
          this.failureCallback(new ErrorModel(ErrorCode.PAYMENT_REQUIRED, this.captchaBox.config.translation.errorPaymentRequired));
          return;
        case 403:
          this.failureCallback(new ErrorModel(ErrorCode.CAPTCHA_NOT_ACCESSIBLE, `The captcha is not accessible from '${location.hostname}'.`));
          return;
        case 404:
          this.failureCallback(new ErrorModel(ErrorCode.CAPTCHA_NOT_FOUND, `No captcha found for site key ${this.captchaBox.config.siteKey}.`));
          return;
        case 422:
          this.failureCallback(new ErrorModel(ErrorCode.MINIMAL_DATA_MODE_MISMATCH, `The minimal-data mode setting of the widget does not match the captcha configuration.`));
          return;
        case 423:
          this.failureCallback(new ErrorModel(ErrorCode.LOCKED, this.captchaBox.config.translation.errorLocked));
          return;
        default:
          this.failureCallback(new ErrorModel(ErrorCode.UNKNOWN_ERROR, this.captchaBox.config.translation.errorUnknown));
          return;
      }
    } catch (error: any) {
      if (this.isReset) return;
      if (error?.name === "AbortError") return;
      this.handleCommunicationFailure();
    }
  }

  private async solveChallenges(challenge: VerificationChallengeDto): Promise<void> {
    if (this.isReset) return;

    try {
      const startSolvingTimestamp = new Date().toISOString();
      const nonces = await this.powSolver.solveTasks(challenge);
      const solvedTimestamp = new Date().toISOString();

      if (this.isReset) return;

      const solutionResponse = await ApiService.submitChallenge(this.captchaBox.config, challenge.verificationId, startSolvingTimestamp, solvedTimestamp, nonces, this.captchaBox.getHoneypotSnapshot(), this.captchaBox.eventTracker.getEventsAndResetArray(), this.abortController.signal);
      if (this.isReset) return;

      switch (solutionResponse.status) {
        case 200: {
          const response: VerificationDto = await solutionResponse.json();
          if (this.isReset) return;
          this.solveChallenges(response.challenge!);
          return;
        }
        case 201: {
          const response: VerificationDto = await solutionResponse.json();
          if (this.isReset) return;
          this.successCallback(response.finished!.verificationToken, response.finished!.expiresInMs);
          return;
        }
        case 404:
          this.failureCallback(new ErrorModel(ErrorCode.CAPTCHA_NOT_FOUND, `No captcha found for site key ${this.captchaBox.config.siteKey}.`));
          return;
        case 409:
          this.failureCallback(new ErrorModel(ErrorCode.POW_FAILURE, this.captchaBox.config.translation.errorPowFailure));
          return;
        default:
          this.failureCallback(new ErrorModel(ErrorCode.UNKNOWN_ERROR, this.captchaBox.config.translation.errorUnknown));
          return;
      }
    } catch (error: any) {
      if (this.isReset) return;
      if (error?.name === "AbortError") return;
      this.handleCommunicationFailure();
    }
  }

  private handleCommunicationFailure(): void {
    if (!this.captchaBox.config.failoverEnabled) {
      this.failureCallback(new ErrorModel(ErrorCode.COMMUNICATION_FAILURE, this.captchaBox.config.translation.errorCommunicationFailure));
      return;
    }
    const verificationId = generateUuidV4();
    const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    const payload = {verificationId, expiresAt, clientFailover: true};
    const token = btoa(JSON.stringify(payload));
    this.successCallback(token, 15 * 60 * 1000);
  }

  public reset(): void {
    this.isReset = true;
    this.abortController.abort();
    this.powSolver.reset();
  }
}
